// ─────────────────────────────────────────────────────────────
// Savings configuration. Dark theme, renders inside the admin shell.
// Uses your EXISTING config endpoints (adminConfig controller):
//   GET   /mtrade/admin/config?group=savings   (and ?group=features)
//   PATCH /mtrade/admin/config/:key            (body { value })
// Both are admin-gated in the controller. invalidateCache() applies
// changes immediately, so toggles take effect on the next request.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { apiClient } from "../../../api/apiServices";

const KEYS = {
  interestEnabled: "features.savings_interest_enabled",
  flexRate:        "savings.interest.flexible_annual_percent",
  fixedRate:       "savings.interest.fixed_annual_percent",
  penaltyEnabled:  "features.savings_penalty_enabled",
  penaltyPercent:  "savings.early_withdrawal_penalty_percent",
  penaltyCap:      "savings.early_withdrawal_penalty_cap",
  flexFeePercent:  "savings.flexible_fee_percent",
  flexFeeFlat:     "savings.flexible_fee_flat",
  freeDates:       "savings.penalty_free_dates",
  windowStart:     "savings.penalty_free_window_start",
  windowEnd:       "savings.penalty_free_window_end",
};

export default function AdminSavingsSettingsView() {
  const [vals, setVals]   = useState({});
  const [loading, setLoad] = useState(true);
  const [newDate, setNewDate] = useState("");
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    Promise.all([
      apiClient.get("/mtrade/admin/config?group=savings"),
      apiClient.get("/mtrade/admin/config?group=features"),
    ]).then(([s, f]) => {
      const flat = {};
      const collect = (grouped) => Object.values(grouped || {}).flat().forEach(item => { if (item?.key) flat[item.key] = item.value; });
      if (s.data.status) collect(s.data.data);
      if (f.data.status) collect(f.data.data);
      setVals(flat);
    }).catch(() => {}).finally(() => setLoad(false));
  }, []);

  const save = async (key, value) => {
    setSavingKey(key);
    try {
      await apiClient.patch(`/mtrade/admin/config/${encodeURIComponent(key)}`, { value });
      setVals(v => ({ ...v, [key]: value }));
    } catch {} finally { setSavingKey(null); }
  };

  const dates = Array.isArray(vals[KEYS.freeDates]) ? vals[KEYS.freeDates]
    : typeof vals[KEYS.freeDates] === "string" ? vals[KEYS.freeDates].split(",").map(s => s.trim()).filter(Boolean) : [];

  const addDate = () => { if (!newDate || dates.includes(newDate)) return; save(KEYS.freeDates, [...dates, newDate].sort()); setNewDate(""); };
  const removeDate = (d) => save(KEYS.freeDates, dates.filter(x => x !== d));

  const Toggle = ({ k, label, sub }) => (
    <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
      <div className="pr-3"><p className="text-white font-semibold text-sm">{label}</p>{sub && <p className="text-white/40 text-xs mt-0.5">{sub}</p>}</div>
      <button onClick={() => save(k, !vals[k])}
        className={`w-12 h-7 rounded-full relative transition-colors flex-shrink-0 ${vals[k] ? "bg-[#00C896]" : "bg-white/10"}`}>
        <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${vals[k] ? "left-6" : "left-1"}`} />
      </button>
    </div>
  );

  const NumberField = ({ k, label, sub }) => (
    <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4">
      <p className="text-white font-semibold text-sm">{label}</p>
      {sub && <p className="text-white/40 text-xs mb-1">{sub}</p>}
      <input type="number" defaultValue={vals[k]} onBlur={e => save(k, Number(e.target.value))}
        className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#00C896]/50 mt-1.5 transition-colors" />
      {savingKey === k && <p className="text-[#00C896] text-[10px] mt-1">Saved</p>}
    </div>
  );

  const SectionTitle = ({ children }) => (
    <h2 className="text-white/30 text-[11px] font-semibold uppercase tracking-widest mb-2">{children}</h2>
  );

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-[#00C896] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="text-white max-w-3xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">Savings Settings</h1>
        <p className="text-white/40 text-sm mt-0.5">Interest, penalties, fees and penalty-free dates</p>
      </div>

      <div className="space-y-6">
        <section className="space-y-3">
          <SectionTitle>Interest (regulated — keep off until confirmed)</SectionTitle>
          <Toggle k={KEYS.interestEnabled} label="Pay interest on savings" sub="Master switch. Leave OFF until your regulatory position is confirmed." />
          <div className="grid grid-cols-2 gap-3">
            <NumberField k={KEYS.flexRate} label="Flexible rate (%/yr)" />
            <NumberField k={KEYS.fixedRate} label="Fixed rate (%/yr)" />
          </div>
        </section>

        <section className="space-y-3">
          <SectionTitle>Early-withdrawal penalty (fixed goals)</SectionTitle>
          <Toggle k={KEYS.penaltyEnabled} label="Charge penalty on early break" sub="Off = fixed goals break free." />
          <div className="grid grid-cols-2 gap-3">
            <NumberField k={KEYS.penaltyPercent} label="Penalty (%)" />
            <NumberField k={KEYS.penaltyCap} label="Hard cap (%)" sub="Safety ceiling" />
          </div>
        </section>

        <section className="space-y-3">
          <SectionTitle>Flexible withdrawal fee (optional)</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <NumberField k={KEYS.flexFeePercent} label="Fee (%)" sub="0 = free" />
            <NumberField k={KEYS.flexFeeFlat} label="Flat fee (₦)" sub="0 = free" />
          </div>
        </section>

        <section className="space-y-3">
          <SectionTitle>Penalty-free withdrawal dates</SectionTitle>
          <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 space-y-3">
            <div className="flex gap-2">
              <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                className="flex-1 bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#00C896]/50 transition-colors [color-scheme:dark]" />
              <button onClick={addDate} className="bg-[#00C896] hover:bg-[#00b386] text-black px-4 rounded-xl text-sm font-semibold transition-colors">Add</button>
            </div>
            {dates.length === 0 ? <p className="text-white/30 text-xs">No free dates set. Fixed goals always incur the penalty when broken early.</p>
            : <div className="flex flex-wrap gap-2">
                {dates.map(d => (
                  <span key={d} className="flex items-center gap-1.5 bg-[#00C896]/15 text-[#00C896] text-xs font-semibold px-2.5 py-1.5 rounded-lg">
                    {new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                    <button onClick={() => removeDate(d)} className="hover:text-white"><i className="ri-close-line" /></button>
                  </span>
                ))}
              </div>}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <p className="text-white/40 text-xs mb-1">Window start (optional)</p>
                <input type="time" defaultValue={vals[KEYS.windowStart]} onBlur={e => save(KEYS.windowStart, e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#00C896]/50 [color-scheme:dark]" />
              </div>
              <div>
                <p className="text-white/40 text-xs mb-1">Window end (optional)</p>
                <input type="time" defaultValue={vals[KEYS.windowEnd]} onBlur={e => save(KEYS.windowEnd, e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#00C896]/50 [color-scheme:dark]" />
              </div>
            </div>
            <p className="text-white/30 text-[11px]">Leave both times blank for the whole day. Times are Africa/Lagos.</p>
          </div>
        </section>
      </div>
    </div>
  );
}
